import React, { useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import DataCard, { tableHeaderCellSx } from "../components/DataCard";
import usePaginatedList from "../hooks/usePaginatedList";
import { KV, DetailDialog, Pre, RequestLink } from "./ActivityParts";
import { fmtDateTime, fmtJson } from "./activityFilters";

const LEVELS = ["DEBUG", "INFO", "WARNING", "ERROR", "CRITICAL"];
const DEFAULT_LEVELS = ["INFO", "WARNING", "ERROR", "CRITICAL"];
const LEVEL_COLOR = { DEBUG: "default", INFO: "info", WARNING: "warning", ERROR: "error", CRITICAL: "error" };

/** Logs tab: the app's persisted log records (applog). Kept 7 days. */
export default function LogsPanel({ requestId, since, onRequestId, initialQuery = "" }) {
  const [levels, setLevels] = useState(DEFAULT_LEVELS);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selected, setSelected] = useState(null);

  const { records, total, loading, error, page, setPage, pageSize, setPageSize, q, setQ } =
    usePaginatedList("/log-event-list", {
      initialPageSize: 100,
      initialSort: "ts",
      initialOrder: "desc",
      pollMs: autoRefresh ? 4000 : 0,
      initialQuery,
      extraParams: {
        level: levels.length === LEVELS.length ? "" : levels.join(","),
        request_id: requestId,
        since,
      },
    });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
        <TextField size="small" label="Search" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="message / logger / path / request id" sx={{ minWidth: 280, flex: 1 }} />
        <ToggleButtonGroup size="small" value={levels} onChange={(_, v) => setLevels(v.length ? v : DEFAULT_LEVELS)}
                           aria-label="Level">
          {LEVELS.map((l) => <ToggleButton key={l} value={l} title={`Toggle ${l}`}>{l}</ToggleButton>)}
        </ToggleButtonGroup>
        <FormControlLabel
          control={<Switch size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
          label="auto-refresh (4s)"
        />
      </Box>

      {error && <Alert severity="error">Failed to load logs. Please try again.</Alert>}

      <DataCard noPadding>
        {loading && records.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress /></Box>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Time", "Level", "Logger", "Req", "Message"].map((h) => (
                    <TableCell key={h} sx={tableHeaderCellSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No matching log events.
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((r) => (
                    <TableRow key={r.id} hover onClick={() => setSelected(r)} sx={{ cursor: "pointer" }}>
                      <TableCell sx={{ whiteSpace: "nowrap", fontFamily: "monospace" }}>{fmtDateTime(r.ts)}</TableCell>
                      <TableCell><Chip size="small" label={r.level} color={LEVEL_COLOR[r.level] || "default"} variant="outlined" /></TableCell>
                      <TableCell sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.logger || ""}>
                        {r.logger || "·"}
                      </TableCell>
                      <TableCell><RequestLink id={r.request_id} onRequestId={onRequestId} /></TableCell>
                      <TableCell sx={{ fontFamily: "monospace", maxWidth: 560, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                 title={r.message || ""}>
                        {r.message || ""}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => setPageSize(parseInt(e.target.value, 10))}
              rowsPerPageOptions={[50, 100, 200]}
            />
          </TableContainer>
        )}
      </DataCard>

      {selected && (
        <DetailDialog
          title={<><Chip size="small" label={selected.level} color={LEVEL_COLOR[selected.level] || "default"} /> Log event</>}
          onClose={() => setSelected(null)}
          actions={selected.request_id && selected.request_id !== requestId
            ? [{ label: "Show everything from this request", onClick: () => { onRequestId(selected.request_id); setSelected(null); } }]
            : []}
        >
          <KV k="Time" v={selected.ts} />
          <KV k="Service" v={selected.service} />
          <KV k="Logger" v={selected.logger} />
          <KV k="Request" v={selected.request_id} />
          <KV k="Method / Path" v={selected.method && selected.path ? `${selected.method} ${selected.path}` : null} />
          <KV k="Status code" v={selected.status_code} />
          <KV k="User" v={selected.user_id} />
          <Box sx={{ pt: 1 }}><Pre>{selected.message || ""}</Pre></Box>
          {selected.context && Object.keys(selected.context).length > 0 && (
            <Box sx={{ pt: 1 }}><Pre>{fmtJson(selected.context)}</Pre></Box>
          )}
          {selected.traceback && <Box sx={{ pt: 1 }}><Pre tone="error">{selected.traceback}</Pre></Box>}
        </DetailDialog>
      )}
    </Box>
  );
}
